/**
 * AddWarehouseAccountDrawer — orchestrates the "Add account" multi-step flow in a
 * right-side Sheet. It owns the wizard state (via useAddAccountWizard), renders the
 * shared header + the current step body, and — on the configure step — drives the
 * form (held by `formRef`) through Test → Save via the header `actions` slot.
 *
 * Choosing a type lands on one page (ACT2-1031). For a type Lookout creates locally that is the
 * configure page, whose type-or-pick identity fields settle the *bucket* (a `(host, db)` account):
 * picking a synced destination's bucket resolves a representative `Warehouse` to prefill the form,
 * while typed coordinates seed it from the bucket's own dims. For a type created in RudderStack it
 * is the platform page, which also offers to connect an existing RudderStack account.
 *
 * Step bodies, the header, and the wizard state each live in their own file so no
 * single file does everything.
 */
import * as React from 'react';

import { ActionTooltip } from '@/_shared/components/ActionTooltip';
import { Badge } from '@/_shared/components/ui/badge';
import { Button } from '@/_shared/components/ui/button';
import { Callout } from '@/_shared/components/ui/callout';
import { Sheet, SheetContent } from '@/_shared/components/ui/sheet';
import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';

import { useWarehousePermission } from '../accountCreatePermission';
import { PermissionNotice } from '../PermissionNotice';
import type { WarehouseFormHandle } from '../shared/formHandle';
import type { UseWarehouseAccountsReturn, WarehouseBucket } from '../useWarehouseAccounts';
import { SUPPORTED_WAREHOUSE_TYPES, type WarehouseTypeKey } from '../warehouseTypes';
import { ConfigureStep } from './ConfigureStep';
import { PlatformAccountStep, type PlatformSubmitHandle } from './PlatformAccountStep';
import { type SetupVerifyHandle, SnowflakeSetupStep } from './SnowflakeSetupStep';
import { TypeStep } from './TypeStep';
import { routeFor, useAddAccountWizard } from './useAddAccountWizard';
import { WizardActions, WizardHeader } from './WizardHeader';

interface AddWarehouseAccountDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called when a connection was created, before the drawer closes. Not called when it is just closed. */
  onConnected?: () => void;
  /**
   * ACT2-1276: why the RudderStack permission blocks every step's action, or null. The drawer reads
   * the permission itself when this is not given.
   */
  blockedReason?: string | null;
  hook: UseWarehouseAccountsReturn;
  /** Phase 3c: Reconnect a paused connection — open straight on the platform step for its type. */
  reconnect?: {
    connectionId: string;
    typeKey: WarehouseTypeKey;
    connectionKey: string;
    name?: string;
    /** ACT2-1031: the stored Lookout compute warehouse, prefilled in the reconnect setup. */
    lookoutWarehouse?: string | null;
  } | null;
}

export function AddWarehouseAccountDrawer({
  open,
  onOpenChange,
  onConnected,
  blockedReason: blockedReasonProp,
  hook,
  reconnect,
}: AddWarehouseAccountDrawerProps): React.JSX.Element {
  const wiz = useAddAccountWizard();
  const permission = useWarehousePermission();
  const blockedReason = blockedReasonProp !== undefined ? blockedReasonProp : permission.blocked;
  // Combobox popups portal into the sheet so its options stay clickable under the modal layer.
  const sheetContentRef = React.useRef<HTMLDivElement>(null);
  const formRef = React.useRef<WarehouseFormHandle>(null);
  // Phase 3b: types whose accounts this workspace creates in RudderStack take the platform step.
  const { platformAccountCreateFamilies } = useWorkspace();
  const platformRef = React.useRef<PlatformSubmitHandle>(null);
  const [platformBusy, setPlatformBusy] = React.useState(false);
  const [platformLabel, setPlatformLabel] = React.useState('Validate and create');
  const [platformCanSubmit, setPlatformCanSubmit] = React.useState(true);
  const setupRef = React.useRef<SetupVerifyHandle>(null);
  const connected = (): void => {
    onConnected?.();
    onOpenChange(false);
  };
  const [setupBusy, setSetupBusy] = React.useState(false);

  // Reset the wizard when the drawer closes so the next open starts fresh at step 1.
  // The kept-account prompt lives on the page-level hook, so it is cleared here too.
  const { reset: resetWizard } = wiz;
  const { clearRetainedPrompt } = hook;
  React.useEffect(() => {
    if (!open) {
      resetWizard();
      clearRetainedPrompt?.();
    }
  }, [open, resetWizard, clearRetainedPrompt]);

  // Reconnect: start on the platform step for the paused connection's type, once per connection.
  const { selectType } = wiz;
  const reconnectId = reconnect?.connectionId;
  const reconnectTypeKey = reconnect?.typeKey;
  React.useEffect(() => {
    if (!reconnectId) return;
    const meta = SUPPORTED_WAREHOUSE_TYPES.find((m) => m.key === reconnectTypeKey);
    if (meta) selectType(meta, 'platform');
  }, [reconnectId, reconnectTypeKey, selectType]);

  const available = React.useMemo((): WarehouseBucket[] => {
    const type = wiz.type;
    if (!type) return [];
    return hook.buckets.filter((b) => type.backendTypes.includes(b.type.toUpperCase()));
  }, [wiz.type, hook.buckets]);

  // The identity fields warn before the server does; they need the keys already stored.
  const existingConnectionKeys = React.useMemo(() => hook.accounts.map((a) => a.connectionKey), [hook.accounts]);

  // A representative destination is looked up even for hand-typed coordinates: type the dims of
  // an account Lookout has already synced and its stored config still prefills the form, exactly
  // as picking the suggestion would. With none, the per-family fields start empty and editable.
  const handleSelectBucket = (bucket: WarehouseBucket | null): void => {
    if (!wiz.type) return;
    // A new bucket is a new add: a prompt raised for the previous one no longer applies.
    hook.clearRetainedPrompt?.();
    if (!bucket) {
      wiz.selectBucket(null, null);
      return;
    }
    const rep = hook.warehouses.find((w) => w.accountKey === bucket.accountKey) ?? null;
    hook.resetTestResult(hook.deriveTestKey(wiz.type.key, bucket.hostDim, bucket.dbDim));
    wiz.selectBucket(bucket, rep);
  };

  // Totals are named on the LOCAL path only, where the two screens are fixed. The platform path's
  // length is not fixed, and is not known here: a customer-supplied family finishes on the
  // account-details screen, while a RudderStack-generated key (Snowflake today) adds the
  // setup-and-verify screen after it — and which applies is settled inside `PlatformAccountStep`, by
  // the chosen mode's `credentialSource` once the form has loaded, not by the type alone.
  //
  // Naming a total anyway is what made the type screen promise "of 3" before the path was chosen,
  // and what made BOTH the account-details and the setup screen call themselves "Step 2 of 2" on the
  // Snowflake path, where the wizard really does walk type → platform → setup. An ordinal with no
  // total is the one label that is correct on every path.
  const subtitle =
    wiz.step === 'type'
      ? 'Step 1: Select warehouse type'
      : wiz.step === 'platform'
        ? 'Step 2: Account details'
        : wiz.step === 'setup'
          ? 'Step 3: Run the setup SQL, then verify'
          : // "Connect", not "Review": for a bucket with no synced destination to prefill it the
            // required field — Snowflake's compute warehouse, Databricks' HTTP path — starts EMPTY
            // here. A customer told to review will not go looking for one.
            'Step 2 of 2: Connect your account';

  let titleBadge: React.ReactNode = null;
  if (wiz.type && wiz.step !== 'type') {
    const TypeIcon = wiz.type.Icon;
    titleBadge = (
      <Badge variant="outline" className="shrink-0 text-xs text-muted-foreground">
        <TypeIcon className="size-3.5" />
        {wiz.type.label}
      </Badge>
    );
  }

  // Configure-step header actions: Test until the connection tests OK, then Save.
  // The connectivity test is gated at the bucket level (the test exercises the full
  // config blob, schema included, and any edit invalidates it via the form's onChange).
  // Connections are per account bucket: a namespace the user enters lives in the config blob only.
  let actions: React.ReactNode = null;
  // Why the step's own action is locked, said in the step too, so no one is left on a dead form.
  const stepNotice = wiz.step === 'type' ? null : blockedReason;
  if (wiz.step === 'platform') {
    actions = (
      <ActionTooltip show={blockedReason !== null} message={blockedReason ?? ''}>
        <Button
          type="button"
          variant="primary"
          loading={platformBusy}
          disabled={blockedReason !== null || platformBusy || !platformCanSubmit}
          onClick={() => void platformRef.current?.submit()}
          label={platformLabel}
        />
      </ActionTooltip>
    );
  } else if (wiz.step === 'setup') {
    actions = (
      <ActionTooltip show={blockedReason !== null} message={blockedReason ?? ''}>
        <Button
          type="button"
          variant="primary"
          loading={setupBusy}
          disabled={blockedReason !== null || setupBusy}
          onClick={() => setupRef.current?.verify()}
          label="Verify"
        />
      </ActionTooltip>
    );
  } else if (wiz.step === 'configure' && wiz.type && !wiz.bucket) {
    // Nothing to test until the identity fields name an account.
    actions = <Button type="button" variant="primary" disabled label="Test" />;
  } else if (wiz.step === 'configure' && wiz.type && wiz.bucket) {
    const typeKey = wiz.type.key;
    const { hostDim, dbDim } = wiz.bucket;
    const testKey = hook.deriveTestKey(typeKey, hostDim, dbDim);
    const tested = hook.testResults[testKey]?.success;
    const runTest = async (): Promise<void> => {
      if ((await formRef.current?.validate()) && formRef.current)
        hook.testConnection(typeKey, testKey, formRef.current.getValues());
    };
    const runSave = async (): Promise<void> => {
      if ((await formRef.current?.validate()) && formRef.current) {
        hook.saveConnection(typeKey, hostDim, dbDim, formRef.current.getValues(), /* isCreate */ true);
      }
    };
    actions = (
      <ActionTooltip show={blockedReason !== null} message={blockedReason ?? ''}>
        {tested ? (
          <Button
            type="button"
            variant="primary"
            loading={hook.isSaving}
            disabled={blockedReason !== null || hook.isSaving || hook.isTesting}
            onClick={() => void runSave()}
            label="Save"
          />
        ) : (
          <Button
            type="button"
            variant="primary"
            loading={hook.isTesting}
            disabled={blockedReason !== null || hook.isTesting || hook.isSaving}
            onClick={() => void runTest()}
            label="Test"
          />
        )}
      </ActionTooltip>
    );
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent ref={sheetContentRef} side="right" showCloseButton={false} className="w-full gap-0 sm:max-w-drawer">
        <WizardHeader
          showControls={wiz.step !== 'platform'}
          // Reconnect reuses this drawer, and QA saw it titled "Add account" over an empty form with
          // nothing naming the connection it was about to rebind (#821 review).
          title={reconnect ? `Reconnect ${reconnect.name ?? 'connection'}` : 'Add account'}
          badge={titleBadge}
          stepLabel={subtitle}
          onBack={wiz.step === 'type' ? undefined : wiz.back}
          actions={actions}
        />

        <div className="flex-1 overflow-y-auto p-4">
          {wiz.step === 'type' && (
            <TypeStep onSelect={(meta) => wiz.selectType(meta, routeFor(meta, platformAccountCreateFamilies))} />
          )}

          {stepNotice !== null &&
            (blockedReasonProp === undefined || blockedReasonProp === permission.blocked ? (
              <PermissionNotice permission={permission} className="mb-4" />
            ) : (
              <Callout variant="info" className="mb-4">
                {stepNotice}
              </Callout>
            ))}

          {wiz.step === 'platform' && wiz.type && (
            <PlatformAccountStep
              ref={platformRef}
              blockedReason={blockedReason}
              type={wiz.type}
              onBusyChange={setPlatformBusy}
              onCreated={connected}
              onUseLocal={() => wiz.type && wiz.selectType(wiz.type, 'local')}
              onSetupStarted={wiz.showSetup}
              onSubmitLabel={setPlatformLabel}
              onCanSubmitChange={setPlatformCanSubmit}
              reconnectConnectionId={reconnect?.connectionId}
              reconnectConnectionKey={reconnect?.connectionKey}
              connections={hook.accounts}
            />
          )}

          {wiz.step === 'setup' && wiz.setup && (
            <SnowflakeSetupStep
              ref={setupRef}
              setup={wiz.setup}
              onBusyChange={setSetupBusy}
              onVerified={connected}
              onRestart={wiz.restartPlatform}
              reconnectConnectionId={reconnect?.connectionId}
              initialLookoutWarehouse={reconnect?.lookoutWarehouse ?? null}
            />
          )}

          {wiz.step === 'configure' && wiz.type && (
            <ConfigureStep
              type={wiz.type}
              warehouse={wiz.warehouse}
              bucket={wiz.bucket}
              buckets={available}
              existingConnectionKeys={existingConnectionKeys}
              onSelectBucket={handleSelectBucket}
              portalContainer={sheetContentRef}
              hook={hook}
              formRef={formRef}
              // Snowflake routed to the platform step reaches this local form only through its
              // "Your own private key" alternative, so the form must not offer the managed key.
              ownKeyOnly={routeFor(wiz.type, platformAccountCreateFamilies) === 'platform'}
              onReconnected={connected}
              retainedReconnectBlocked={blockedReason}
            />
          )}
        </div>
        {wiz.step === 'platform' && (
          <div className="border-t border-border p-4">
            <WizardActions inFooter onBack={wiz.back} actions={actions} />
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
